import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * GitHub Actions artifact source (Connect GitHub flow).
 *
 * GitHub blocks anonymous artifact downloads even on public repos (401), so
 * the viewer calls GitHub's API directly WITH THE USER'S OAUTH TOKEN
 * (obtained once per device via app/gh-auth.ts):
 *   GET {GH_API_BASE}/repos/{owner}/{repo}/actions/artifacts/{id}/zip
 *     Authorization: Bearer <user token>
 *     → 302 Location: <short-lived signed blob URL>  (api.github.com is
 *       CORS-open; fetch follows the redirect and strips the Authorization
 *       header cross-origin per spec, so the token never reaches Azure)
 *
 * The artifact zip WRAPS the uploaded files and the inner .evidence entry is
 * DEFLATE-compressed, so no nested Range streaming is possible: the outer zip
 * is downloaded whole (bounded), the single .evidence entry is inflated, and
 * the resulting Blob opens through the ordinary file path. Producer contract:
 * exactly one .evidence per artifact — zero or multiple is an error.
 */

import { GH_API_BASE } from '../config';
import { getGhToken } from '../app/gh-auth';
import { HttpByteSource } from '../pack/byte-source';
import { MeteredByteSource } from '../pack/metered';
import { openPackZip } from '../pack/zip';
import { isAbort, isPackError, PackError, toPackError } from '../pack/errors';
var GH_NAME = /^[A-Za-z0-9_.-]+$/;
var GH_ID = /^\d+$/;
/** GitHub's 403 body when the target org restricts third-party OAuth apps:
 * "…the `<org>` organization has enabled OAuth App access restrictions…".
 * There is no distinguishing header for this case, so the body is the signal
 * (read once at the fetch layer — the UI still maps by code, not message). */
var ORG_OAUTH_RESTRICTION = /OAuth App access restrictions/i;

/** Recode generic transport errors into gh-specific codes; aborts and
 * content errors pass through untouched. `ssoHeader` disambiguates a SAML
 * SSO 403 (GitHub sends x-github-sso with an org-authorize URL); `body403`
 * disambiguates an OAuth-App-access-restriction 403 (org must approve). */
function recode(e, ref, ssoHeader, body403) {
  if (isAbort(e) || !isPackError(e)) return e;
  var detail = _objectSpread(_objectSpread({}, e.detail), ref);
  if (e.code === 'fetch-failed') {
    return new PackError('gh-network-error', e.message, detail);
  }
  if (e.code === 'http-error') {
    var _e$detail;
    var status = typeof ((_e$detail = e.detail) === null || _e$detail === void 0 ? void 0 : _e$detail.status) === 'number' ? e.detail.status : 0;
    if (status === 401) {
      return new PackError('gh-auth-required', e.message, detail);
    }
    if (status === 403 && ssoHeader) {
      // Header shape: "required; url=https://github.com/orgs/<org>/sso?…"
      var m = /url=(\S+)/.exec(ssoHeader);
      return new PackError('gh-sso-required', e.message, _objectSpread(_objectSpread({}, detail), {}, {
        ssoUrl: m ? m[1] : undefined
      }));
    }
    if (status === 403 && body403 && ORG_OAUTH_RESTRICTION.test(body403)) {
      return new PackError('gh-org-approval-required', e.message, detail);
    }
    return new PackError('gh-artifact-error', e.message, detail);
  }
  if (e.code === 'pack-too-large') {
    return new PackError('gh-artifact-too-large', e.message, detail);
  }
  return e;
}

/**
 * Extract a single .evidence entry from an artifact zip container. Shared by
 * the gh-artifact fetch AND the file-drop path.
 *
 * - 0 packs → `gh-no-evidence-entry`.
 * - `entry` given → open that pack (matched by full name or basename);
 *   missing → `gh-multiple-evidence-entries` so the chooser reappears.
 * - exactly 1 → open it.
 * - several, no `entry` → `gh-multiple-evidence-entries` carrying the list
 *   (the store routes this to the CHOOSER, not the error screen).
 */
export function extractEvidenceFromArtifact(_x, _x2, _x3) {
  return _extractEvidenceFromArtifact.apply(this, arguments);
}

/**
 * Download the artifact from GitHub (user token) and extract its single
 * .evidence entry. Returns the pack as a Blob plus the entry's name.
 */
function _extractEvidenceFromArtifact() {
  _extractEvidenceFromArtifact = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(container, label, opts) {
    var all, packs, choices, chosen, basename, entry, bytes, entryName;
    return _regeneratorRuntime.wrap(function (_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          all = container.listEntries();
          packs = all.filter(function (e) {
            return e.name.toLowerCase().endsWith('.evidence');
          });
          if (!(packs.length === 0)) {
            _context.next = 1;
            break;
          }
          throw new PackError('gh-no-evidence-entry', "".concat(label, " contains no .evidence file"), _objectSpread(_objectSpread({}, opts === null || opts === void 0 ? void 0 : opts.detail), {}, {
            entries: all.map(function (e) {
              return e.name;
            })
          }));
        case 1:
          choices = packs.map(function (e) {
            return {
              name: e.name,
              size: e.size
            };
          });
          chosen = packs.length === 1 ? packs[0] : undefined;
          if (opts !== null && opts !== void 0 && opts.entry) {
            basename = function basename(n) {
              return n.split('/').pop() || n;
            };
            chosen = packs.find(function (e) {
              return e.name === opts.entry || basename(e.name) === opts.entry;
            });
          }
          if (chosen) {
            _context.next = 2;
            break;
          }
          throw new PackError('gh-multiple-evidence-entries', "".concat(label, " contains ").concat(packs.length, " .evidence files"), _objectSpread(_objectSpread({}, opts === null || opts === void 0 ? void 0 : opts.detail), {}, {
            entries: choices
          }));
        case 2:
          entry = chosen.name;
          _context.next = 3;
          return container.readBytes(entry, {
            signal: opts === null || opts === void 0 ? void 0 : opts.signal
          });
        case 3:
          bytes = _context.sent;
          if (bytes) {
            _context.next = 4;
            break;
          }
          throw new PackError('corrupt-entry', "unreadable entry ".concat(entry), {
            entry: entry
          });
        case 4:
          entryName = entry.split('/').pop() || 'pack.evidence';
          return _context.abrupt("return", {
            blob: new Blob([bytes], {
              type: 'application/zip'
            }),
            entryName: entryName
          });
        case 5:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _extractEvidenceFromArtifact.apply(this, arguments);
}
export function fetchGhArtifactPack(_x4, _x5) {
  return _fetchGhArtifactPack.apply(this, arguments);
}
function _fetchGhArtifactPack() {
  _fetchGhArtifactPack = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3(ref, opts) {
    var _opts$apiBase, _opts$fetchImpl;
    var owner, repo, artifactId, base, token, url, inner, ssoHeader, body403, authedFetch, raw, metered, outer, _t2;
    return _regeneratorRuntime.wrap(function (_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          owner = ref.owner, repo = ref.repo, artifactId = ref.artifactId; // Defensive re-validation — refs normally arrive via parseGhArtifact.
          if (!(!GH_NAME.test(owner) || !GH_NAME.test(repo) || !GH_ID.test(artifactId))) {
            _context3.next = 1;
            break;
          }
          throw new PackError('gh-artifact-error', 'malformed artifact reference', _objectSpread({}, ref));
        case 1:
          base = ((_opts$apiBase = opts === null || opts === void 0 ? void 0 : opts.apiBase) !== null && _opts$apiBase !== void 0 ? _opts$apiBase : GH_API_BASE).replace(/\/$/, '');
          token = (opts === null || opts === void 0 ? void 0 : opts.token) !== undefined ? opts.token : getGhToken();
          url = "".concat(base, "/repos/").concat(encodeURIComponent(owner), "/").concat(encodeURIComponent(repo), "/actions/artifacts/").concat(encodeURIComponent(artifactId), "/zip"); // Wrap the fetch to (a) attach the user's token and (b) capture the
          // x-github-sso header off a 403 so recode() can tell SAML-SSO apart
          // from a plain permission/rate 403.
          inner = (_opts$fetchImpl = opts === null || opts === void 0 ? void 0 : opts.fetchImpl) !== null && _opts$fetchImpl !== void 0 ? _opts$fetchImpl : fetch.bind(globalThis);
          ssoHeader = null;
          body403 = null;
          authedFetch = /*#__PURE__*/function () {
            var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(input, init) {
              var res, clone, _t;
              return _regeneratorRuntime.wrap(function (_context2) {
                while (1) switch (_context2.prev = _context2.next) {
                  case 0:
                    _context2.next = 1;
                    return inner(input, _objectSpread(_objectSpread({}, init), {}, {
                      headers: _objectSpread(_objectSpread({}, init === null || init === void 0 ? void 0 : init.headers), token ? {
                        Authorization: "Bearer ".concat(token)
                      } : {})
                    }));
                  case 1:
                    res = _context2.sent;
                    if (!(res.status === 403)) {
                      _context2.next = 6;
                      break;
                    }
                    try {
                      ssoHeader = res.headers.get('x-github-sso');
                    } catch (_unused) {
                      /* header unreadable — plain 403 advice applies */
                    }
                    // Read a clone so HttpByteSource's own body handling is untouched; the
                    // body is the only signal for the org-OAuth-restriction 403 (no header).
                    _context2.prev = 2;
                    clone = typeof res.clone === 'function' ? res.clone() : null;
                    if (!clone) {
                      _context2.next = 4;
                      break;
                    }
                    _context2.next = 3;
                    return clone.text();
                  case 3:
                    body403 = _context2.sent;
                  case 4:
                    _context2.next = 6;
                    break;
                  case 5:
                    _context2.prev = 5;
                    _t = _context2["catch"](2);
                  case 6:
                    return _context2.abrupt("return", res);
                  case 7:
                  case "end":
                    return _context2.stop();
                }
              }, _callee2, null, [[2, 5]]);
            }));
            return function authedFetch(_x6, _x7) {
              return _ref.apply(this, arguments);
            };
          }();
          _context3.prev = 2;
          raw = new HttpByteSource(url, {
            preferWholeBody: true,
            fetchImpl: authedFetch
          });
          metered = new MeteredByteSource(raw, opts === null || opts === void 0 ? void 0 : opts.onStats);
          _context3.next = 3;
          return openPackZip(metered, "".concat(owner, "/").concat(repo, "#").concat(artifactId), {
            signal: opts === null || opts === void 0 ? void 0 : opts.signal
          });
        case 3:
          outer = _context3.sent;
          _context3.next = 4;
          return extractEvidenceFromArtifact(outer, "artifact ".concat(artifactId), {
            signal: opts === null || opts === void 0 ? void 0 : opts.signal,
            entry: opts === null || opts === void 0 ? void 0 : opts.entry,
            detail: _objectSpread({}, ref)
          });
        case 4:
          return _context3.abrupt("return", _context3.sent);
        case 5:
          _context3.prev = 5;
          _t2 = _context3["catch"](2);
          throw toPackError(recode(_t2, ref, ssoHeader, body403), 'gh-network-error');
        case 6:
        case "end":
          return _context3.stop();
      }
    }, _callee3, null, [[2, 5]]);
  }));
  return _fetchGhArtifactPack.apply(this, arguments);
}